/**
 * Vendor Display Utilities
 * 
 * IMPORTANT: Always use these helper functions to display vendor names.
 * DO NOT directly access vendor.business_name - it contains outdated data.
 * The source of truth is vendor.organization.name from the organizations table.
 */

/**
 * Gets the correct display name for a vendor
 * 
 * @param vendor - Vendor profile object
 * @returns The vendor's organization name, with fallback to business_name
 * 
 * @example
 * ```tsx
 * const displayName = getVendorDisplayName(vendor);
 * <h1>{displayName}</h1>
 * ```
 */
export function getVendorDisplayName(vendor: {
  organization_name?: string;
  organization?: { name: string };
  business_name?: string;
} | null | undefined): string {
  if (!vendor) return 'Unknown Vendor';
  
  // Priority order:
  // 1. organization_name (if already enriched by query)
  // 2. organization.name (if organization join exists)
  // 3. business_name (legacy fallback)
  return vendor.organization_name || 
         vendor.organization?.name || 
         vendor.business_name || 
         'Unknown Vendor';
}

/**
 * Type guard to check if vendor has organization data loaded
 */
export function hasOrganizationData(vendor: any): vendor is { organization: { name: string } } {
  return vendor?.organization?.name != null;
}

/**
 * DEPRECATED: Returns business_name with warning
 * @deprecated Use getVendorDisplayName() instead
 */
export function getVendorBusinessName(vendor: { business_name?: string }): string {
  console.warn('[DEPRECATED] getVendorBusinessName() is deprecated. Use getVendorDisplayName() instead.');
  return vendor.business_name || 'Unknown';
}
